import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import AbstractShape from "@/components/AbstractShape";
import { goToIntake } from "@/lib/intake";
import useSEO from "@/lib/useSEO";
import { breadcrumbSchema } from "@/lib/schema";

const whyMelbi = [
  "Simple online access designed for convenience and clarity",
  "Licensed clinician review before prescription treatment",
  "Licensed pharmacy fulfillment if prescribed",
  "Transparent experience with no insurance billing",
];

export default function WhyMelbi() {
  useSEO({
    title: "Why Melbi | Clinician-Guided Wellness, No Insurance Needed",
    description:
      "Simple online access, licensed clinician review, licensed pharmacy fulfillment, flat-rate pricing — the Melbi approach to clinician-guided wellness.",
    url: "https://melbiwellness.com/why-melbi",
    jsonLd: [
      breadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "Why Melbi", url: "/why-melbi" },
      ]),
    ],
  });
  return (
    <div data-testid="why-melbi-page">
      {/* HERO */}
      <section className="relative">
        <AbstractShape
          variant="gradient-warm"
          colors={["#F5E6D3", "#E8D5C4", "#D4DCD7"]}
          className="absolute inset-0"
          testId="why-melbi-hero-bg"
        />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-12 md:pt-24 md:pb-16 text-center">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8A9A92]">
            Why Melbi
          </p>
          <h1
            data-testid="why-melbi-hero-headline"
            className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#2C2B29] leading-[1.05] max-w-4xl mx-auto"
          >
            A simpler way to access{" "}
            <span className="italic text-[#C96A4E]">trusted wellness options.</span>
          </h1>
          <p className="mt-5 text-[#5E5C58] text-lg leading-relaxed max-w-2xl mx-auto">
            Focused wellness offerings. Transparent pricing. Licensed clinician
            review and licensed pharmacy fulfillment when prescribed.
          </p>
        </div>
      </section>

      {/* WHAT MELBI STANDS FOR */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
        <div className="grid lg:grid-cols-5 gap-10 items-center">
          <div className="lg:col-span-2 relative aspect-square rounded-3xl overflow-hidden">
            <img
              src="/lifestyle/our-story.png"
              alt="A healthy moment at home, exploring trusted wellness options"
              data-testid="why-melbi-story-img"
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-tr from-[#2C2B29]/15 via-transparent to-transparent" />
          </div>
          <div className="lg:col-span-3" data-testid="what-melbi-stands-for-content">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#8A9A92]">
              What Melbi Stands For
            </p>
            <h2 className="mt-3 font-display text-3xl md:text-4xl font-semibold tracking-tight text-[#2C2B29] leading-tight">
              Curated wellness options from{" "}
              <span className="italic text-[#C96A4E] pl-0.5">sources you can trust.</span>
            </h2>
            <p className="mt-5 text-[#5E5C58] leading-relaxed">
              Melbi was created to make access to wellness options feel simpler,
              more credible, and more transparent. We focus on carefully selected
              wellness offerings, transparent pricing, licensed clinician review
              when appropriate, and licensed pharmacy fulfillment when prescribed.
            </p>
            <p className="mt-4 text-[#5E5C58] italic leading-relaxed">
              Clinical review is required when appropriate, and not everyone will qualify.
            </p>

            <div
              data-testid="why-melbi-trust-callout"
              className="mt-6 bg-white border border-[#E8E5DF] rounded-2xl p-5 md:p-6 shadow-[0_4px_24px_rgba(0,0,0,0.04)]"
            >
              <h3 className="font-display text-lg md:text-xl font-semibold text-[#2C2B29] leading-snug">
                No gray-market sourcing. No guessing games.
              </h3>
              <p className="mt-2 text-sm md:text-[15px] text-[#5E5C58] leading-relaxed">
                Melbi was built around licensed clinician review,
                patient-specific prescriptions when appropriate, and licensed
                U.S. pharmacy fulfillment. Patients receive clear instructions
                and access to message the care team with questions along the
                way.
              </p>
              <Link
                to="/trust-your-source"
                data-testid="why-melbi-trust-link"
                className="mt-3 inline-flex items-center text-sm font-semibold text-[#C96A4E] hover:gap-2 gap-1 transition-all"
              >
                Learn what makes Melbi different
                <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#F5E6D3] via-[#F3F1EC] to-[#E8D5C4]" aria-hidden="true" />
        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[#C96A4E]/10 blur-3xl" aria-hidden="true" />
        <div className="absolute -bottom-32 -left-24 w-[28rem] h-[28rem] rounded-full bg-[#8A9A92]/10 blur-3xl" aria-hidden="true" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28 text-center">
          <h2
            data-testid="final-cta-headline"
            className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#2C2B29] leading-[1.05]"
          >
            Ready to take control of your{" "}
            <span className="text-[#C96A4E] italic">wellness?</span>
          </h2>
          <p className="mt-6 text-[#5E5C58] text-lg leading-relaxed max-w-xl mx-auto">
            Begin your clinician-guided wellness journey today.
          </p>
          <button
            data-testid="why-melbi-start-now-cta"
            className="mt-8 inline-flex items-center justify-center bg-[#C96A4E] text-white rounded-full px-9 py-4 text-base font-semibold hover:bg-[#B35A40] transition-all shadow-[0_8px_24px_rgba(201,106,78,0.25)] hover:shadow-[0_12px_32px_rgba(201,106,78,0.35)] hover:-translate-y-0.5"
            type="button"
            onClick={goToIntake}
          >
            Start now
            <ArrowRight size={18} className="ml-2" />
          </button>
        </div>
      </section>
    </div>
  );
}
